import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PaymentLinksFilters } from "./PaymentLinks/PaymentLinksFilters";
import { PaymentLinksTable } from "./PaymentLinks/PaymentLinkCard";
import { CreatePaymentLinkModal } from "./PaymentLinks/CreatePaymentLinkModal";
import { PaymentLinkDetailsModal } from "./PaymentLinks/PaymentLinkDetailsModal";
import { ResendMethodModal } from "./PaymentLinks/ResendMethodModal";
import { Close, ContentCopy } from '@mui/icons-material';
import AdminSidebar from "../../components/admin/AdminSidebar";
import DashboardHeader from "../../components/admin/DashboardHeader";
import { useAuth } from "../../contexts/AuthContext";
import { fetchPaymentLinks, createPaymentLink, renewPaymentLink, fetchPaymentLinkDetails, mapPaymentLinkFromApi } from "../../services/paymentLinkService";
import { fetchCompanyPos } from "../../services/posService";
import { hosts } from "../../config/environment";

const mockPaymentLinks = [
    {
        id: 'PL-2024-001',
        title: 'Paiement Abonnement Premium',
        amount: 299.00,
        currency: 'USD',
        client: 'Boutique Flow Principal',
        clientContact: 'boutique@flow.com',
        message: 'Paiement pour votre abonnement mensuel',
        status: 'paid',
        paymentMethod: 'card',
        createdAt: '2024-01-15',
        expiresAt: '2024-01-22',
        paidAt: '2024-01-16',
        link: 'https://flowpos.com/pay/pl-2024-001',
        notificationMethods: ['email', 'whatsapp'],
        clicks: 15,
        payments: 1
    },
    {
        id: 'PL-2024-002',
        title: 'Achat de matériel',
        amount: 1500.00,
        currency: 'USD',
        client: 'Restaurant Flow Express',
        clientContact: '+243 81 234 5678',
        message: 'Paiement pour équipement de cuisine',
        status: 'pending',
        paymentMethod: null,
        createdAt: '2024-01-18',
        expiresAt: '2024-01-25',
        paidAt: null,
        link: 'https://flowpos.com/pay/pl-2024-002',
        notificationMethods: ['sms'],
        clicks: 8,
        payments: 0
    },
    {
        id: 'PL-2024-003',
        title: 'Formation Team',
        amount: 899.00,
        currency: 'EUR',
        client: 'Company ABC',
        clientContact: 'hr@companyabc.com',
        message: 'Paiement pour session de formation équipe',
        status: 'expired',
        paymentMethod: null,
        createdAt: '2024-01-10',
        expiresAt: '2024-01-17',
        paidAt: null,
        link: 'https://flowpos.com/pay/pl-2024-003',
        notificationMethods: ['email', 'whatsapp'],
        clicks: 12,
        payments: 0
    },
    {
        id: 'PL-2024-004',
        title: 'Service Consulting',
        amount: 2500.00,
        currency: 'USD',
        client: 'Entreprise XYZ',
        clientContact: 'contact@xyz.com',
        message: 'Honoraires consulting stratégique',
        status: 'collected',
        paymentMethod: 'bank_transfer',
        createdAt: '2024-01-12',
        expiresAt: '2024-01-19',
        paidAt: '2024-01-14',
        link: 'https://flowpos.com/pay/pl-2024-004',
        notificationMethods: ['email'],
        clicks: 6,
        payments: 1
    }
];
const PaymentLinks = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedOrganization } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [paymentLinks, setPaymentLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [fromArticle, setFromArticle] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedLink, setSelectedLink] = useState(null);
    const [showResendModal, setShowResendModal] = useState(false);
    const [linkToResend, setLinkToResend] = useState(null);
    const [posList, setPosList] = useState([]);
    const [showPermanentModal, setShowPermanentModal] = useState(false);
    const [selectedTerminalId, setSelectedTerminalId] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (selectedOrganization?.id) {
            loadPaymentLinks();
        }
    }, [selectedOrganization, filterStatus, searchTerm]);

    useEffect(() => {
        if (selectedOrganization?.id && showCreateModal) {
            fetchCompanyPos(selectedOrganization.id).then(setPosList).catch(() => setPosList([]));
        }
    }, [selectedOrganization?.id, showCreateModal]);

    useEffect(() => {
        if (selectedOrganization?.id && showPermanentModal) {
            fetchCompanyPos(selectedOrganization.id).then((list) => {
                setPosList(list || []);
                if (!selectedTerminalId && Array.isArray(list) && list.length > 0) {
                    setSelectedTerminalId(String(list[0].id));
                }
            }).catch(() => setPosList([]));
        }
    }, [selectedOrganization?.id, showPermanentModal, selectedTerminalId]);

    const selectedTerminal = useMemo(() => {
        if (!selectedTerminalId) return null;
        return (posList || []).find((p) => String(p.id) === String(selectedTerminalId)) || null;
    }, [posList, selectedTerminalId]);

    const permanentUrl = useMemo(() => {
        if (!selectedTerminal?.tag || !hosts.checkout) return '';
        return `${hosts.checkout}/payment-terminals/${selectedTerminal.tag}`;
    }, [selectedTerminal?.tag]);

    const copyPermanentUrl = async () => {
        if (!permanentUrl) return;
        try {
            await navigator.clipboard.writeText(permanentUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            console.error('Copy permanent URL failed:', e);
            alert('Impossible de copier le lien.');
        }
    };

    const sharePermanentUrl = async () => {
        if (!permanentUrl) return;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Lien de paiement permanent',
                    text: `Payer via ce lien : ${permanentUrl}`,
                    url: permanentUrl
                });
                return;
            } catch (e) {
                if (e?.name !== 'AbortError') {
                    await copyPermanentUrl();
                }
                return;
            }
        }
        await copyPermanentUrl();
    };

    // Ouvrir le modal de création avec article prérempli (depuis la page Articles)
    useEffect(() => {
        const article = location.state?.fromArticle;
        if (article) {
            setFromArticle(article);
            setShowCreateModal(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.fromArticle, location.pathname, navigate]);

    const loadPaymentLinks = async () => {
        if (!selectedOrganization?.id) return;
        
        try {
        setLoading(true);
            const user = JSON.parse(localStorage.getItem('netikash_user') || '{}');
            const isAdmin = user.permissions?.includes('admin') || false;
            
            const response = await fetchPaymentLinks({
                pge: 1,
                lmt: 100,
                sts: filterStatus === 'all' ? 'all' : filterStatus,
                org: selectedOrganization.id,
                emt: isAdmin ? 'all' : user.id,
                cmr: 'all',
                slg: searchTerm || ''
            });

            const linksData = response.contents || [];
            
            // Transformer les données de l'API en format attendu par l'UI
            const links = linksData.map(mapPaymentLinkFromApi).filter(Boolean);

            setPaymentLinks(links);
        } catch (error) {
            console.error('Error loading payment links:', error);
            setPaymentLinks([]);
        } finally {
            setLoading(false);
        }
    };

    // Filtrer les liens de paiement
    const filteredLinks = paymentLinks.filter(link => {
        const matchesSearch = !searchTerm ||
            link.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            link.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            link.client?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            link.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            link.customer?.phoneNumber?.includes(searchTerm) ||
            link.customer?.email?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = filterStatus === 'all' || link.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    // Créer un nouveau lien de paiement et l'envoyer au client (SMS / Email / WhatsApp) — payload aligné mobile
    const handleCreatePaymentLink = async (paymentLinkData) => {
        if (!selectedOrganization?.id) {
            alert('Organisation non sélectionnée');
            return;
        }
        try {
            const data = {
                ...paymentLinkData,
                company_id: paymentLinkData.company_id || selectedOrganization.id,
            };
            await createPaymentLink(data);
            await loadPaymentLinks();
            setShowCreateModal(false);
            const method = paymentLinkData.method || 'sms';
            const methodLabel = { sms: 'SMS', email: 'Email', whatsapp: 'WhatsApp' }[method] || method;
            const customer = paymentLinkData.customer;
            const dest = method === 'email' ? (customer?.email || '') : (customer ? `${customer.zip || ''} ${customer.phoneNumber || ''}`.trim() : '');
            alert(`Lien de paiement créé et envoyé par ${methodLabel} à ${customer?.name || dest || 'le client'}.`);
        } catch (error) {
            console.error('Error creating payment link:', error);
            alert(error.message || 'Erreur lors de la création du lien de paiement');
        }
    };

    // Voir les détails d'un lien
    const handleViewDetails = async (link) => {
        try {
            // Charger les détails complets depuis l'API
            const details = await fetchPaymentLinkDetails(link.id);
            setSelectedLink(mapPaymentLinkFromApi({ ...link, ...(details || {}) }));
            setShowDetailsModal(true);
        } catch (error) {
            console.error('Error loading link details:', error);
            // Utiliser les données déjà disponibles si l'API échoue
            setSelectedLink(link);
            setShowDetailsModal(true);
        }
    };

    // Copier le lien de paiement
    const handleCopyLink = async (link) => {
        try {
            const linkUrl = link.link || `${window.location.origin}/pay/${link.id}`;
            await navigator.clipboard.writeText(linkUrl);
            alert('Lien copié dans le presse-papiers !');
        } catch (error) {
            console.error('Error copying link:', error);
            alert('Erreur lors de la copie du lien');
        }
    };

    // Renouveler le lien (renew)
    const handleRenewLink = async (link) => {
        try {
            const response = await renewPaymentLink(link.id);
            
            if (response?.error) {
                alert(response.message || 'Erreur lors du renouvellement du lien');
                return;
            }

            alert('Lien renouvelé avec succès !');
            // Recharger la liste
            await loadPaymentLinks();
            // Recharger les détails si le modal est ouvert
            if (showDetailsModal && selectedLink?.id === link.id) {
                const details = await fetchPaymentLinkDetails(link.id);
                setSelectedLink(mapPaymentLinkFromApi({ ...link, ...(details || {}) }));
            }
        } catch (error) {
            console.error('Error renewing link:', error);
            alert(error.message || 'Erreur lors du renouvellement du lien');
        }
    };

    // Renvoyer le lien : ouvrir le modal de choix du canal (SMS / Email / WhatsApp), puis ouvrir l'app correspondante (pas de copie presse-papier)
    const handleResendLink = (link) => {
        setLinkToResend(link);
        setShowResendModal(true);
    };

    // Annuler le lien (marquer comme expiré)
    const handleCancelLink = async (link) => {
        if (!window.confirm('Êtes-vous sûr de vouloir annuler ce lien de paiement ?')) {
            return;
        }

        try {
            // Note: L'API pourrait avoir un endpoint pour annuler, pour l'instant on marque juste comme expiré localement
            // Si l'API a un endpoint DELETE ou PUT pour annuler, l'utiliser ici
        setPaymentLinks(prev => prev.map(pl =>
            pl.id === link.id ? { ...pl, status: 'expired' } : pl
        ));
            alert('Lien annulé');
        } catch (error) {
            console.error('Error cancelling link:', error);
            alert('Erreur lors de l\'annulation du lien');
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="payment-links"
            />

            <div className="flex-1 min-w-0 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="min-h-screen p-4 sm:p-6">
                    <DashboardHeader
                        timeRange={timeRange}
                        onTimeRangeChange={setTimeRange}
                        currency={currency}
                        onCurrencyChange={setCurrency}
                        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                    />

                    <div className="space-y-6 mt-6">
                        {/* Header Principal avec Bannière */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 sm:p-6 text-amber-900 shadow-sm">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                <div className="mb-4 md:mb-0">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <div className="p-2 bg-amber-500/10 rounded-xl">
                                            <span className="text-xl">🔗</span>
                                        </div>
                                        <h1 className="text-2xl font-bold text-amber-900">Liens de Paiement</h1>
                                    </div>
                                    <p className="text-amber-700">
                                        Générez et gérez vos liens de paiement instantanés
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Filtres */}
                        <PaymentLinksFilters
                            searchTerm={searchTerm}
                            onSearchChange={setSearchTerm}
                            filterStatus={filterStatus}
                            onFilterChange={setFilterStatus}
                            onCreateLink={() => setShowCreateModal(true)}
                            onSharePermanentLink={() => setShowPermanentModal(true)}
                        />

                        {/* État de chargement */}
                        {loading ? (
                            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm animate-pulse">
                                <div className="space-y-4">
                                    <div className="h-8 bg-slate-300 rounded w-1/4 mb-4"></div>
                                    {[1, 2, 3, 4, 5].map((item) => (
                                        <div key={item} className="flex items-center space-x-4 py-4 border-b border-slate-200">
                                            <div className="h-12 bg-slate-300 rounded w-12"></div>
                                            <div className="flex-1 space-y-2">
                                                <div className="h-4 bg-slate-300 rounded w-1/3"></div>
                                                <div className="h-3 bg-slate-300 rounded w-1/4"></div>
                                            </div>
                                            <div className="h-6 bg-slate-300 rounded w-20"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Tableau des liens de paiement - UN SEUL COMPOSANT */}
                                <PaymentLinksTable
                                    links={filteredLinks}  // ✅ Passe le tableau filtré
                                    onViewDetails={handleViewDetails}
                                    onCopyLink={handleCopyLink}
                                    onResendLink={handleResendLink}
                                    onCancelLink={handleCancelLink}
                                />

                                {/* Message si aucun lien trouvé */}
                                {!loading && filteredLinks.length === 0 && (
                                    <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 shadow-sm">
                                        <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-slate-300">
                                            <span className="text-3xl text-slate-400">🔗</span>
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-800 mb-3">
                                            {searchTerm || filterStatus !== 'all'
                                                ? 'Aucun lien trouvé'
                                                : 'Aucun lien de paiement'
                                            }
                                        </h3>
                                        <p className="text-slate-600 max-w-md mx-auto mb-6">
                                            {searchTerm || filterStatus !== 'all'
                                                ? 'Aucun lien ne correspond à vos critères de recherche.'
                                                : 'Créez votre premier lien de paiement pour commencer.'
                                            }
                                        </p>
                                        <button
                                            onClick={() => setShowCreateModal(true)}
                                            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-sm hover:shadow-md"
                                        >
                                            <span className="mr-2">+</span>
                                            Créer un lien de paiement
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Modals */}
                    <CreatePaymentLinkModal
                        show={showCreateModal}
                        onClose={() => { setShowCreateModal(false); setFromArticle(null); }}
                        onCreate={handleCreatePaymentLink}
                        terminals={posList}
                        initialArticle={fromArticle}
                    />

                    <PaymentLinkDetailsModal
                        show={showDetailsModal}
                        onClose={() => {
                            setShowDetailsModal(false);
                            setSelectedLink(null);
                        }}
                        link={selectedLink}
                        onCopyLink={handleCopyLink}
                        onRenewLink={handleRenewLink}
                        onResendLink={handleResendLink}
                        onCancelLink={handleCancelLink}
                    />

                    <ResendMethodModal
                        show={showResendModal}
                        onClose={() => {
                            setShowResendModal(false);
                            setLinkToResend(null);
                        }}
                        link={linkToResend}
                    />

                    {/* Modal lien permanent (sans montant) */}
                    {showPermanentModal && (
                        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                            <div
                                className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
                                onClick={() => setShowPermanentModal(false)}
                                aria-hidden="true"
                            />
                            <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-slate-200/80 overflow-hidden">
                                <div className="px-6 py-5 bg-gradient-to-r from-amber-500 to-orange-500">
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="min-w-0">
                                            <h2 className="text-xl font-bold text-white truncate">Lien de paiement permanent</h2>
                                            <p className="text-amber-100 text-sm">Sans montant prédéfini (par terminal)</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowPermanentModal(false)}
                                            className="p-2 rounded-xl hover:bg-white/20 text-white transition-colors"
                                            aria-label="Fermer"
                                        >
                                            <Close className="text-2xl" />
                                        </button>
                                    </div>
                                </div>

                                <div className="p-6 space-y-4">
                                    {!hosts.checkout ? (
                                        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl px-4 py-3 text-sm font-medium">
                                            L’URL de paiement n’est pas configurée. Ajoutez `REACT_APP_PAY_WEB_URL` dans l’environnement.
                                        </div>
                                    ) : null}

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-slate-700">Terminal</label>
                                        <select
                                            value={selectedTerminalId}
                                            onChange={(e) => setSelectedTerminalId(e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:ring-2 focus:ring-amber-400 focus:border-amber-400 bg-white"
                                        >
                                            {(posList || []).map((t) => (
                                                <option key={t.id} value={t.id}>
                                                    {t.name || 'Terminal'}{t.tag ? ` (${t.tag})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {posList?.length === 0 && (
                                            <p className="text-xs text-slate-500">Aucun terminal trouvé pour cette organisation.</p>
                                        )}
                                    </div>

                                    <div className="rounded-2xl bg-amber-50/70 border border-amber-200 p-4">
                                        <p className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-2">Lien</p>
                                        <div className="flex items-center gap-2">
                                            <input
                                                readOnly
                                                value={permanentUrl || ''}
                                                className="flex-1 px-4 py-3 rounded-xl border border-amber-200 bg-white text-slate-800 text-sm font-medium"
                                            />
                                            <button
                                                type="button"
                                                onClick={copyPermanentUrl}
                                                disabled={!permanentUrl}
                                                className="p-3 rounded-xl bg-white border border-amber-200 text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-50"
                                                title="Copier"
                                            >
                                                <ContentCopy className="text-lg" />
                                            </button>
                                        </div>
                                        {copied && (
                                            <p className="text-xs text-emerald-700 font-semibold mt-2">Lien copié.</p>
                                        )}
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                        <button
                                            type="button"
                                            onClick={sharePermanentUrl}
                                            disabled={!permanentUrl}
                                            className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50"
                                        >
                                            Partager
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowPermanentModal(false)}
                                            className="flex-1 py-3 px-4 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
                                        >
                                            Fermer
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentLinks;